import { useCallback, useRef, useState } from "react";

const MIN_DISPLAY_TIME = 2000;

const getRemainingTime = (startTime: number | null) => {
  if (!startTime) return 0;
  return Math.max(0, MIN_DISPLAY_TIME - (Date.now() - startTime));
};

export const useCopilotLoadingTextQueue = () => {
  const [displayedLoadingText, setDisplayedLoadingText] = useState<
    string | null
  >(null);
  const queueRef = useRef<string[]>([]);
  const currentTextStartTimeRef = useRef<number | null>(null);
  const processingRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const processQueue = useCallback(() => {
    if (processingRef.current || queueRef.current.length === 0) {
      return;
    }

    processingRef.current = true;
    const nextText = queueRef.current[0];
    queueRef.current = queueRef.current.slice(1);

    const remainingTime = getRemainingTime(currentTextStartTimeRef.current);

    timeoutRef.current = setTimeout(() => {
      setDisplayedLoadingText(nextText);
      currentTextStartTimeRef.current = Date.now();
      processingRef.current = false;
      timeoutRef.current = null;

      if (queueRef.current.length > 0) {
        processQueue();
      }
    }, remainingTime);
  }, []);

  const clearLoadingText = useCallback(() => {
    clearPendingTimeout();
    queueRef.current = [];
    processingRef.current = false;

    const remainingTime = getRemainingTime(currentTextStartTimeRef.current);

    if (remainingTime > 0) {
      timeoutRef.current = setTimeout(() => {
        setDisplayedLoadingText(null);
        currentTextStartTimeRef.current = null;
        timeoutRef.current = null;
      }, remainingTime);
    } else {
      setDisplayedLoadingText(null);
      currentTextStartTimeRef.current = null;
    }
  }, [clearPendingTimeout]);

  const enqueueLoadingText = useCallback(
    (text: string | null) => {
      if (text === null) {
        clearLoadingText();
        return;
      }

      if (text === displayedLoadingText) {
        return;
      }

      if (displayedLoadingText === null && !processingRef.current) {
        setDisplayedLoadingText(text);
        currentTextStartTimeRef.current = Date.now();
        return;
      }

      if (!queueRef.current.includes(text)) {
        queueRef.current = [...queueRef.current, text];

        if (!processingRef.current) {
          processQueue();
        }
      }
    },
    [clearLoadingText, displayedLoadingText, processQueue],
  );

  return {
    copilotLoadingText: displayedLoadingText,
    enqueueLoadingText,
  };
};
